import React, { useMemo } from "react";
import { MdEventSeat } from "react-icons/md";
import "../../../styles/SeatSelection.scss";

export interface SeatTicket {
  id: string;
  ticket_type: string;
  price: string;
  available_slot: string;
  ticket_count?: number;
  capacity?: number;
}

interface SeatSelectionProps {
  tickets: SeatTicket[];
  selectedSeatIds: string[];
  disabled?: boolean;

  maxSelectableSeats?: number;
  showSeatLimitNote?: boolean;

  onSeatSelect: (
    ticket: SeatTicket
  ) => void;
}

interface ParsedSeat {
  ticket: SeatTicket;
  rowNumber: number;
  seatNumber: number;
  rowLabel: string;
  seatLabel: string;
}

interface SeatRow {
  rowNumber: number;
  rowLabel: string;
  seats: ParsedSeat[];
}

/**
 * Supported ticket names:
 *
 * R1-S1
 * R1-S-1
 * R-1-S1
 * R-1-S-1
 */
const parseSeatName = (
  ticket: SeatTicket
): ParsedSeat | null => {
  const seatName = ticket.ticket_type
    ?.trim()
    .toUpperCase();

  if (!seatName) {
    return null;
  }

  const match = seatName.match(
    /^R-?(\d+)\s*-\s*S-?(\d+)$/
  );

  if (!match) {
    return null;
  }

  const rowNumber = Number(match[1]);
  const seatNumber = Number(match[2]);

  if (
    Number.isNaN(rowNumber) ||
    Number.isNaN(seatNumber)
  ) {
    return null;
  }

  return {
    ticket,
    rowNumber,
    seatNumber,
    rowLabel: `R-${rowNumber}`,
    seatLabel: `S-${seatNumber}`,
  };
};

const formatPrice = (
  value:
    | string
    | number
    | null
    | undefined
) => {
  const price = Number(value);

  if (Number.isNaN(price)) {
    return "0.00";
  }

  return price.toLocaleString(
    "en-PH",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

const SeatSelection: React.FC<
  SeatSelectionProps
> = ({
  tickets,
  selectedSeatIds,
  disabled = false,
  maxSelectableSeats,
  showSeatLimitNote = false,
  onSeatSelect,
}) => {
  const EventSeatIcon =
    MdEventSeat as React.ElementType;

  const seatPrice =
    tickets.length > 0
      ? formatPrice(
          tickets[0].price
        )
      : "0.00";

  const hasSeatLimit =
    typeof maxSelectableSeats ===
      "number" &&
    maxSelectableSeats >= 0;

  const hasReachedSeatLimit =
    hasSeatLimit &&
    selectedSeatIds.length >=
      maxSelectableSeats;

  const remainingSeatCount =
    hasSeatLimit
      ? Math.max(
          maxSelectableSeats -
            selectedSeatIds.length,
          0
        )
      : 0;

  const seatRows =
    useMemo<SeatRow[]>(() => {
      const rowMap = new Map<
        number,
        ParsedSeat[]
      >();

      tickets.forEach((ticket) => {
        const parsedSeat =
          parseSeatName(ticket);

        if (!parsedSeat) {
          return;
        }

        const existingRow =
          rowMap.get(
            parsedSeat.rowNumber
          ) || [];

        existingRow.push(
          parsedSeat
        );

        rowMap.set(
          parsedSeat.rowNumber,
          existingRow
        );
      });

      return Array.from(
        rowMap.entries()
      )
        .sort(
          (
            [firstRow],
            [secondRow]
          ) =>
            firstRow - secondRow
        )
        .map(
          ([rowNumber, seats]) => ({
            rowNumber,
            rowLabel: `R-${rowNumber}`,
            seats: seats.sort(
              (
                firstSeat,
                secondSeat
              ) =>
                firstSeat.seatNumber -
                secondSeat.seatNumber
            ),
          })
        );
    }, [tickets]);

  const ungroupedSeats =
    useMemo(() => {
      return tickets.filter(
        (ticket) =>
          !parseSeatName(ticket)
      );
    }, [tickets]);

  if (
    seatRows.length === 0 &&
    ungroupedSeats.length === 0
  ) {
    return (
      <div className="seat-selection-empty">
        No available seats.
      </div>
    );
  }

  const renderSeat = (
    ticket: SeatTicket,
    seatLabel: string,
    seatNumber?: number
  ) => {
    const availableSlots = Number(
      ticket.available_slot || 0
    );

    const isSoldOut =
      availableSlots <= 0;

    const isSelected =
      selectedSeatIds.includes(
        ticket.id
      );

    /*
     * Kapag na-reach na ang seat limit,
     * selected seats remain clickable
     * para maaari pa rin silang
     * i-unselect.
     */
    const isLimitDisabled =
      hasReachedSeatLimit &&
      !isSelected;

    const isDisabled =
      disabled ||
      isSoldOut ||
      isLimitDisabled;

    let title = seatLabel;

    if (isSoldOut) {
      title = `${seatLabel} - Sold Out`;
    } else if (isLimitDisabled) {
      title = `${seatLabel} - Seat limit reached`;
    }

    return (
      <label
        key={ticket.id}
        className={[
          "event-seat",
          isSelected
            ? "selected"
            : "",
          isSoldOut
            ? "sold-out"
            : "",
          isLimitDisabled
            ? "limit-disabled"
            : "",
          isDisabled
            ? "disabled"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        title={title}
      >
        <input
          type="checkbox"
          name={`event-seat-${ticket.id}`}
          value={ticket.id}
          checked={isSelected}
          disabled={isDisabled}
          onChange={() =>
            onSeatSelect(ticket)
          }
        />

        <span className="event-seat-icon">
          <EventSeatIcon />
        </span>

        <span className="event-seat-label">
          {seatLabel}
        </span>

        {typeof seatNumber ===
          "number" && (
          <span className="sr-only">
            Seat {seatNumber}
          </span>
        )}
      </label>
    );
  };

  return (
    <div
      className={[
        "seat-selection",
        disabled
          ? "seat-selection-disabled"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="seat-selection-header">
        <div className="seat-legend">
          <div className="seat-legend-item">
            <span className="seat-legend-icon available">
              <EventSeatIcon />
            </span>

            <span>Available</span>
          </div>

          <div className="seat-legend-item">
            <span className="seat-legend-icon selected">
              <EventSeatIcon />
            </span>

            <span>Selected</span>
          </div>

          <div className="seat-legend-item">
            <span className="seat-legend-icon sold">
              <EventSeatIcon />
            </span>

            <span>Sold</span>
          </div>
        </div>

        <div className="seat-price-summary">
          <span>Price:</span>

          <strong>
            ₱{seatPrice} / seat
          </strong>
        </div>

        {showSeatLimitNote &&
          hasSeatLimit && (
            <div className="seat-limit-note">
              Please choose{" "}
              <strong>
                {maxSelectableSeats}{" "}
                {maxSelectableSeats ===
                1
                  ? "seat"
                  : "seats"}
              </strong>{" "}
              based on the number of
              tickets you are buying.
              You have selected{" "}
              <strong>
                {selectedSeatIds.length}
              </strong>{" "}
              of{" "}
              <strong>
                {maxSelectableSeats}
              </strong>
              .
              {remainingSeatCount > 0
                ? ` Please select ${remainingSeatCount} more ${
                    remainingSeatCount ===
                    1
                      ? "seat"
                      : "seats"
                  }.`
                : " Your seat selection is complete."}
            </div>
          )}
      </div>

      <div className="seat-map">
        <div className="seat-rows">
          {seatRows.map((row) => (
            <div
              key={row.rowNumber}
              className="seat-row"
            >
              <div className="seat-row-label">
                {row.rowLabel}
              </div>

              <div className="seat-row-items">
                {row.seats.map(
                  ({
                    ticket,
                    seatNumber,
                    seatLabel,
                  }) =>
                    renderSeat(
                      ticket,
                      seatLabel,
                      seatNumber
                    )
                )}
              </div>
            </div>
          ))}

          {ungroupedSeats.length >
            0 && (
            <div className="seat-row">
              <div className="seat-row-label">
                Others
              </div>

              <div className="seat-row-items">
                {ungroupedSeats.map(
                  (ticket) =>
                    renderSeat(
                      ticket,
                      ticket.ticket_type
                    )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeatSelection;